/* eslint-disable react-hooks/exhaustive-deps */
// import React from 'react';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  useColorStyle,
  useColorMode,
  Checkbox,
  TextLabel,
  Tooltip,
  Icon,
} from '@tonic-one/react';
import { FileCopyOIcon } from '@tonic-one/react-icons';
import IconButton from 'src/components/IconButton';
import { Info } from 'src/components/Common/Info';
import { useTranslation } from 'react-i18next';
import CSpinner from 'src/components/CSpinner';
import ErrorAlert from 'src/components/Common/ErrorAlert';
import { useOrgStore } from 'src/models/useOrgModel';
import { GrantedAccess } from '../Common/GrantedAccess';
import { PolicyType } from '../../../types/app';
import { ProvisionApi, GlobalApi } from '@Apis';
import {
  GmailInlineProtectionSettings,
  OtherGmailInlineProtectionSettings,
} from '../utils/types';
import { GmailInlineSetting } from 'src/types/provision';
import { usePermissionStore } from '@Models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';

const InlineProtectionSettingsForGmail = React.memo(() => {
  const { widthEditablePermission } = usePermissionStore();
  const withEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [gmailInlineOtherSettings, setGmailInlineOtherSettings] =
    useState<GmailInlineProtectionSettings>({
      senderAddressMatchSettings: {
        envelopeAddressEnable: false,
        messageAddressEnable: false,
      },
      trustScanEnable: false,
    });
  const { orgId } = useOrgStore();
  const [isShowMultiService, setIsShowMultiService] =
    React.useState<boolean>(false);
  const [selectedPolicyType, setSelectedPolicyType] =
    React.useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [gmailInlineSetting, setGmailInlineSetting] =
    useState<GmailInlineSetting>({
      inboundHostname: '',
      outboundHostname: '',
      inboundLoopHeader: '',
      outboundLoopHeader: '',
    });
  const { t } = useTranslation();
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      setErrorMsg(t('common.apiException.internalError'));
    }
  };
  useEffect(() => {
    (async function doCall() {
      await loadData();
    })();
  }, [orgId]);

  const updateGmailInlineProtectionSettings = async (
    gmailInlineProtectionSettings: GmailInlineProtectionSettings
  ) => {
    try {
      setLoading(true);
      await GlobalApi.setGmailInlineProtectionSettings({
        envelopeAddressEnable:
          gmailInlineProtectionSettings.senderAddressMatchSettings
            .envelopeAddressEnable,
        messageAddressEnable:
          gmailInlineProtectionSettings.senderAddressMatchSettings
            .messageAddressEnable,
        trustScanEnable: gmailInlineProtectionSettings.trustScanEnable,
      });
    } catch (error) {
      setErrorMsg(t('common.apiException.internalError'));
    }
    setLoading(false);
  };

  async function loadData() {
    try {
      setLoading(true);
      const policyTypes = (
        await ProvisionApi.getProvisionedServicesByOrgId(orgId)
      ).data;
      const result = policyTypes.filter((item) =>
        [PolicyType.GmailInline].includes(item)
      );
      if (result.length === 0) {
        setIsShowMultiService(false);
        setSelectedPolicyType(PolicyType.GmailInline.toString());
      } else {
        setIsShowMultiService(true);
        const inlineProtectionSettingsForGmailTmp: OtherGmailInlineProtectionSettings =
          (await GlobalApi.getGmailInlineProtectionSettings()).data;
        setGmailInlineOtherSettings({
          senderAddressMatchSettings: {
            envelopeAddressEnable:
              inlineProtectionSettingsForGmailTmp.envelopeAddressEnable,
            messageAddressEnable:
              inlineProtectionSettingsForGmailTmp.messageAddressEnable,
          },
          trustScanEnable: inlineProtectionSettingsForGmailTmp.trustScanEnable,
        });
        const gmailInlineProtectionInfo: GmailInlineSetting = (
          await GlobalApi.getGmailInlineProtectionInfo()
        ).data;
        setGmailInlineSetting(gmailInlineProtectionInfo);
      }
    } catch (error) {
      setErrorMsg(t('common.apiException.internalError'));
    } finally {
      setLoading(false);
    }
  }

  const handleGmailInlineSenderAddressChange = async (e: any) => {
    try {
      setLoading(true);
      const gmailInlineOtherSettingsTmp = {
        senderAddressMatchSettings: {
          envelopeAddressEnable:
            gmailInlineOtherSettings.senderAddressMatchSettings
              .envelopeAddressEnable,
          messageAddressEnable: e.target.checked,
        },
        trustScanEnable: gmailInlineOtherSettings.trustScanEnable,
      };
      setGmailInlineOtherSettings(gmailInlineOtherSettingsTmp);
      await updateGmailInlineProtectionSettings(gmailInlineOtherSettingsTmp);
    } catch (err) {
      setErrorMsg(t('common.apiException.internalError'));
    } finally {
      setLoading(false);
    }
  };

  const handleGmailInlineTrustScanChange = async (e: any) => {
    try {
      setLoading(true);
      const gmailInlineOtherSettingsTmp = {
        senderAddressMatchSettings: {
          envelopeAddressEnable:
            gmailInlineOtherSettings.senderAddressMatchSettings
              .envelopeAddressEnable,
          messageAddressEnable:
            gmailInlineOtherSettings.senderAddressMatchSettings
              .messageAddressEnable,
        },
        trustScanEnable: e.target.checked,
      };
      setGmailInlineOtherSettings(gmailInlineOtherSettingsTmp);
      await updateGmailInlineProtectionSettings(gmailInlineOtherSettingsTmp);
    } catch (err) {
      setErrorMsg(t('common.apiException.internalError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box px="4x">
      {loading && <CSpinner />}
      {errorMsg && (
        <ErrorAlert
          errorMsg={errorMsg}
          onClose={() => setErrorMsg(undefined)}
        />
      )}
      {isShowMultiService ? (
        <Box>
          <Text
            mt="28px"
            fontSize="lg"
            lineHeight="lg"
            color={colorStyle.color.primary}
          >
            {t('globalSetting.gmailInline.title')}
          </Text>
          <Flex mt="4px" flexDirection="column">
            <Text
              mt="28px"
              fontSize="lg"
              lineHeight="lg"
              color={colorStyle.color.primary}
            >
              {t('globalSetting.gmailInline.inbound.title')}
            </Text>
            <Flex mt="16px" alignItems="center">
              <TextLabel>
                {t('globalSetting.gmailInline.inbound.hostname')}:{' '}
              </TextLabel>
              <Text ml="2x">{gmailInlineSetting.inboundHostname}</Text>
              <IconButton ml="2x">
                <Tooltip label={t('policy.tooltip.copy')}>
                  <Icon
                    as={FileCopyOIcon}
                    onClick={() =>
                      copyToClipboard(gmailInlineSetting.inboundHostname)
                    }
                  />
                </Tooltip>
              </IconButton>
            </Flex>
            <Flex mt="16px" alignItems="center">
              <TextLabel>
                {t('globalSetting.gmailInline.inbound.header')}:{' '}
              </TextLabel>
              <Text ml="2x">{gmailInlineSetting.inboundLoopHeader}</Text>
              <IconButton ml="2x">
                <Tooltip label={t('policy.tooltip.copy')}>
                  <Icon
                    as={FileCopyOIcon}
                    onClick={() =>
                      copyToClipboard(gmailInlineSetting.inboundLoopHeader)
                    }
                  />
                </Tooltip>
              </IconButton>
            </Flex>
          </Flex>
          <Flex mt="20px" flexDirection="column">
            <Text
              fontSize="lg"
              lineHeight="lg"
              color={colorStyle.color.primary}
            >
              {t('globalSetting.gmailInline.outbound.title')}
            </Text>
            <Flex mt="16px" alignItems="center">
              <TextLabel>
                {t('globalSetting.gmailInline.outbound.hostname')}:{' '}
              </TextLabel>
              <Text ml="2x">{gmailInlineSetting.outboundHostname}</Text>
              <IconButton ml="2x">
                <Tooltip label={t('policy.tooltip.copy')}>
                  <Icon
                    as={FileCopyOIcon}
                    onClick={() =>
                      copyToClipboard(gmailInlineSetting.outboundHostname)
                    }
                  />
                </Tooltip>
              </IconButton>
            </Flex>
            <Flex mt="16px" alignItems="center">
              <TextLabel>
                {t('globalSetting.gmailInline.outbound.header')}:{' '}
              </TextLabel>
              <Text ml="2x">{gmailInlineSetting.outboundLoopHeader}</Text>
              <IconButton ml="2x">
                <Tooltip label={t('policy.tooltip.copy')}>
                  <Icon
                    as={FileCopyOIcon}
                    onClick={() =>
                      copyToClipboard(gmailInlineSetting.outboundLoopHeader)
                    }
                  />
                </Tooltip>
              </IconButton>
            </Flex>
          </Flex>
          <Flex mt="28px">
            <Text
              fontSize="lg"
              lineHeight="lg"
              color={colorStyle.color.primary}
            >
              {t('globalSetting.gmailInline.senderAddressMatch.title')}
            </Text>
          </Flex>
          <Flex mt="20px">
            <Text
              color={colorStyle.color.secondary}
              lineHeight="lg"
              maxWidth={1000}
            >
              {t('globalSetting.gmailInline.senderAddressMatch.desc')}
            </Text>
          </Flex>
          <Flex m="16px 0px">
            <Checkbox
              disabled
              checked={
                gmailInlineOtherSettings.senderAddressMatchSettings
                  .envelopeAddressEnable
              }
            ></Checkbox>
            <Text ml="8px">
              {t('globalSetting.gmailInline.senderAddressMatch.envelope.title')}
            </Text>
            <Info
              iconStyle={{ ml: '2x' }}
              content={t(
                'globalSetting.gmailInline.senderAddressMatch.envelope.tooltip'
              )}
            />
          </Flex>
          <Flex m="16px 0px">
            <Checkbox
              disabled={!withEditPermission}
              checked={
                gmailInlineOtherSettings.senderAddressMatchSettings
                  .messageAddressEnable
              }
              onChange={handleGmailInlineSenderAddressChange}
            ></Checkbox>
            <Text ml="8px">
              {t('globalSetting.gmailInline.senderAddressMatch.message.title')}
            </Text>
            <Info
              iconStyle={{ ml: '2x' }}
              content={t(
                'globalSetting.gmailInline.senderAddressMatch.message.tooltip'
              )}
            />
          </Flex>
          <Flex mt="32px">
            <Text
              fontSize="lg"
              lineHeight="lg"
              color={colorStyle.color.primary}
            >
              {t('globalSetting.exchangeInline.trustScan.title')}
            </Text>
          </Flex>
          <Flex m="16px 0px">
            <Checkbox
              disabled={!withEditPermission}
              checked={gmailInlineOtherSettings.trustScanEnable}
              onChange={handleGmailInlineTrustScanChange}
            ></Checkbox>
            <Text ml="8px">
              {t('globalSetting.exchangeInline.trustScan.content')}
            </Text>
            <Info
              iconStyle={{ ml: '2x' }}
              content={t('globalSetting.exchangeInline.trustScan.tooltip')}
            />
          </Flex>
        </Box>
      ) : null}
      {!isShowMultiService && !loading ? (
        <GrantedAccess
          selectedPolicyType={selectedPolicyType}
          orgId={orgId}
        ></GrantedAccess>
      ) : null}
    </Box>
  );
});
export default InlineProtectionSettingsForGmail;
